import { Button, ConfigProvider, DatePicker, Form, Input, InputNumber, message, Modal, Select, Progress, Result } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { WaterInvoiceBasic, WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { PaymentWithAccountantPayload, WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { PaymentWithAccountantMethod } from "@/core/enums/global.enums";
import { paymentWithAccountantMethodPipe } from "@/core/pipes/pay-accountant-method.pipe";
import { currencyFormatter, formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";
import { DownloadOutlined } from "@ant-design/icons";
import jsonToCsvExport from "json-to-csv-export";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { toApiTimestamp, DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterInvoiceBasic[],
}

export interface PaymentWithAccountantMultiPayload {
    paymentMethod?: string;
    information: string;
    total: number;
    actualReceivedDate?: string;
}

interface FailedTransaction {
    id: string;
    waterUserName: string;
    waterUserCode: string;
    errorMessage: string;
}

const INPUT_WIDTH = 450;

const headers = [
    { key: "id", label: "ID" },
    { key: "waterUserName", label: "Mã KH" },
    { key: "waterUserCode", label: "Tên KH" },
    { key: "errorMessage", label: "Lỗi" },
  ];

export const MakeMultiAccPaymentModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const [form] = Form.useForm<PaymentWithAccountantMultiPayload>();
    const [loading, setLoading] = useState<boolean>(false);
    const [finished, setFinished] = useState<boolean>(false);
    const [processed, setProcessed] = useState<string[]>([]);
    const [failed, setFailed] = useState<FailedTransaction[]>([]);

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldValue("actualReceivedDate", null);
        setProcessed([]);
        setFailed([]);
        setFinished(false);
    }
    useEffect(() => {
        handleDataChange();
    }, [data])



    // Initialize select options data
    const init = async () => { }

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            message.warning({
                content: "Đang xử lý thanh toán, vui lòng không đóng cửa sổ này..."
            });
            const actualReceivedDate = toApiTimestamp(formData.actualReceivedDate);
            for (let i = 0; i < data.length; i++) {
                if (!isModalOpen) break;
                const invoice = data[i];
                const result = await waterInvoiceRepo.payWithAccountant({
                information: formData.information,
                paymentMethod: formData.paymentMethod,
                total: invoice.total,
                id: invoice.id,
                actualReceivedDate
                }, false);

                if (result === true) {
                    setProcessed(prev => [...prev, invoice.id]);
                } else {
                    setFailed(prev => [...prev, {
                        id: invoice.id,
                        waterUserName: invoice.waterUserName,
                        waterUserCode: invoice.waterUserCode,
                        errorMessage: typeof result === "string" ? result : "Thanh toán gạch nợ thất bại!"
                    }]);
                }
            }
            setFinished(true);
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal open={isModalOpen} onCancel={handleCancel} width={520} centered
            styles={{
                mask: {
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'var(--primary-mask)'
                }
            }}
            footer={[
                <div key={1} className="w-full flex flex-row justify-end px-1.5">
                    {processed.length === 0 && failed.length === 0&&<Button key="submit" type="primary" loading={loading}
                        style={{ 
                            background: "var(--primary-color)", 
                            paddingRight: 48, 
                            paddingLeft: 48, 
                            fontSize: 18,
                            fontWeight: 600,
                            paddingTop: 18, 
                            paddingBottom: 18,
                            }}
                        onClick={handleSave}
                    >
                        THANH TOÁN
                    </Button>}
                </div>,
            ]}>
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">GẠCH NỢ NHIỀU HOÁ ĐƠN</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<PaymentWithAccountantMultiPayload>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >
                        <div>
                            {(processed.length === 0&&failed.length === 0) &&<div>
                                <div className="text-lg pb-2">
                                    <p><b>Số lượng hoá đơn được chọn: <span style={{color: "var(--primary-color)"}}>{data.length}</span></b></p>
                                </div>
                                <Form.Item<PaymentWithAccountantMultiPayload>
                                    label="Loại hình Thanh toán"
                                    name="paymentMethod"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select showSearch placeholder="Chọn Hình thức thanh toán" style={{width: INPUT_WIDTH}}>
                                    {Object.keys(PaymentWithAccountantMethod).map(opt => <Select.Option key={opt} value={opt}>{paymentWithAccountantMethodPipe(opt)}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<PaymentWithAccountantMultiPayload>
                                    label="Ngày nhận tiền thực tế"
                                    name="actualReceivedDate"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>
                                <Form.Item<PaymentWithAccountantMultiPayload>
                                    label="Thông tin giao dịch"
                                    name="information"
                                >
                                    <Input.TextArea style={{width: INPUT_WIDTH, height: 202, resize: "none"}}></Input.TextArea>
                                </Form.Item>
                            </div>}
                            <div>
                                {(processed.length > 0 || failed.length > 0)&&!finished && (
                                    <div className="mb-4">
                                        <p>Đang thực hiện gạch nợ cho {data.length} hoá đơn...</p>
                                        <Progress
                                            percent={Math.round(((processed.length + failed.length) / data.length) * 100)}
                                            status="normal"
                                            format={() => `${processed.length + failed.length} / ${data.length}`}
                                        />
                                    </div>
                                )}
                                {finished && (
                                    <Result
                                        status={processed.length > 0 ? "success" : "error"}
                                        title={`Đã thực hiện giao dịch gạch nợ cho ${processed.length}/${data.length} hoá đơn!`}
                                        subTitle="Giao dich đã hoàn tất, bạn có thể đóng cửa sổ này."
                                    />
                                )}
                                {failed.length > 0 && (
                                    <div className="mt-4">
                                        <div className="flex flex-row items-center justify-between mb-2">
                                            <p className="text-red-600 font-bold mb-2">Giao dịch thất bại: ({failed.length})</p>
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                danger
                                                onClick={() => jsonToCsvExport({
                                                    data: failed, headers
                                                })}>Tải xuống file lỗi</Button>
                                        </div>
                                        <table className="w-full border border-red-400 text-sm">
                                            <thead>
                                                <tr className="bg-red-100">
                                                    <th className="border border-red-300 px-2 py-1">Mã KH</th>
                                                    <th className="border border-red-300 px-2 py-1">Tên KH</th>
                                                    <th className="border border-red-300 px-2 py-1">Lỗi</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {failed.map((item, idx) => (
                                                    <tr key={item.id} className={idx % 2 === 0 ? "bg-white" : "bg-red-50"}>
                                                        <td className="border border-red-200 px-2 py-1">{item.waterUserCode}</td>
                                                        <td className="border border-red-200 px-2 py-1">{item.waterUserName}</td>
                                                        <td className="border border-red-200 px-2 py-1 text-red-700">{item.errorMessage}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}